import * as React from 'react';
import * as styles from './Phonebook.css';
import { PeopleCard, IPeopleCardProperties } from './peopleCard/PeopleCard';
import { IPeopleInfo } from '../../interfaces/IPeopleInfo';
import { IPeopleProperty } from '../../interfaces/IPeopleProperty';
import { ConditionalOutput } from '../../services/ConditionalOutput';
import { Environment } from '../../services/Environment';
import PhonebookService from '../../services/PhonebookService';
import { LocalizationService } from '../../services/LocalizationService';
import { IAIcon } from '../icon/Icon';

export interface IProps {
  tenant: string;
  token: string;
  environment: "Development" | "Test" | "Production";
  siteCollection: string;
  highlightColor: string;
  showTitle: boolean;
  title?: string;
  titleColor?: string;
  isMobile?: boolean;
  resultSourceId?: string;
}

export interface IState {
  content: IPeopleInfo[];
  searchQuery: string;
  isLoading: boolean;
  loadingMore: boolean;
  hasAllPeople: boolean;
}

export class IAPhonebook extends React.Component<IProps, IState> {
  private rowLimit = 10;
  private phonebookSearchInput: React.RefObject<any>;
  constructor(props) {
    super(props);
    this.state = {
      content: [],
      searchQuery: '',
      isLoading: true,
      hasAllPeople: false,
      loadingMore: false,
    };

    this.phonebookSearchInput = React.createRef();
    this.search("", 0);
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.siteCollection !== this.props.siteCollection) {
      this.search("", 0, props.siteCollection);
    }
  }

  private getPropertiesForPeopleCard = (content: IPeopleProperty[]): IPeopleCardProperties[] => {
    const result: IPeopleCardProperties[] = [];
    content.map(property => {
      if (property?.property && property?.value) {
        result.push({
          value: property.value,
          property: property.property,
          classNames: new ConditionalOutput(["IA_credentials_dots"])
            .OnCondition(property.property === "PreferredName", ["IA_credentials_dots", "IA_credential_name"])
            .Last(),
          iconTitle: new ConditionalOutput('Link')
            .OnCondition(property.property === 'PreferredName', "")
            .OnCondition(property.property === 'WorkEmail', 'Email')
            .OnCondition(property.property === 'JobTitle', 'Star')
            .OnCondition(property.property === 'Department', 'Organization')
            .OnCondition(property.property === 'WorkPhone', 'Phone')
            .OnCondition(property.property === 'MobilePhone', 'Phone')
            .OnCondition(/[,]/.test(property.property) && /Phone/.test(property.property), 'Phone')
            .Last(),
          link: property.property === 'WorkPhone' || property.property === 'MobilePhone' ? "tel:" :property.type,
          iconColor: 'gray',
          iconSize: 20,
          templateLink: property.templateLink,
        });
      }
    });
    return result;
  }

  private async search(searchQuery: string, startRow?: number, siteCollection?: string): Promise<void> {
    if (startRow) {
      this.setState({ loadingMore: true });
    } else {
      this.setState({ isLoading: true, content: [] });
    }
    const row = startRow || 0;
    const resultSource = this.props.resultSourceId ? this.props.resultSourceId: "";
    const unicodedSearchQuery = searchQuery?.replace(/[+]/gi, '%2b') || "";
    const url = `${Environment.getEnvironmentForMainAPI(this.props.environment)}/api/${this.props.tenant}.sharepoint.com/${siteCollection !== undefined ? siteCollection : this.props.siteCollection}/phonebook/token/${this.props.token}?searchQuery=${unicodedSearchQuery}&resultSource=${resultSource}&rowLimit=${this.rowLimit}&startRow=${row}`;
    const [content, searchedQuery]: [IPeopleInfo[], string] = await PhonebookService.getPhonebook(url, this.props.token)
      .then((json: any) => [json.people as IPeopleInfo[], json.searchQuery as string]);
    const isLastSearchedRequest = searchQuery === this.state.searchQuery;
    let data = content && content.length > 0 ? content : [];

    
    if (row !== 0) { 
      //The getPhonebook call might return people/persons that are already received in a previous call (even when using the startRow to skip items)
      //Therefore we use a filter on the newly received dataset which removes all the people that are already in state which have the same "Path" property value
      //The "Path" property value is ensured to be unique, as this is the given item's URL path
      const duplicatePersonsRemoved = data.filter(person => {
        const personPath = person.properties.find(p => p.property === "Path")?.value;
        if (!personPath) return false;
  
        const isDuplicate = this.state.content.some(existingPerson => 
          existingPerson.properties.find(prop => prop.property === "Path")?.value === personPath
        );
      
        return !isDuplicate;
      });

      data = this.state.content.concat(duplicatePersonsRemoved); 
    }
    if (searchedQuery === undefined || isLastSearchedRequest) {
      this.setState({
        content: data,
        searchQuery: searchedQuery,
        isLoading: false,
        loadingMore: false,
        hasAllPeople: content && content.length < this.rowLimit
      });
    }
  }
  public render() {
    let table: JSX.Element;
    table = (
      <div
        className={styles.phonebookContent}
        style={{
          height: "100%"
        }}
      >
        <div
          className={styles.phonebookHeader}
          style={{
            height: this.props.showTitle ? 100 : 60,
            color: this.props.titleColor != undefined ? this.props.titleColor : "#333333"
          }}
        >
          {this.props.showTitle &&
            <div className={styles.phonebookTitle}> {this.props.title != undefined ? this.props.title : new LocalizationService().strings.Phonebook_Header}</div>
          }
          <div className={styles.phonebookSearch}>
            <input
              placeholder={new LocalizationService().strings.Phonebook_Search}
              ref={this.phonebookSearchInput}
              onChange={(str) => {
                this.setState({ searchQuery: str.target.value });
                this.search(str.target.value);
              }}
              value={this.state.searchQuery || ""}
            />
          </div>
          <IAIcon
            title="Close"
            size={18}
            color={"gray"}
            style={{
              marginTop: -52,
              float: "right",
              marginRight: 7,
              zIndex: 901
            }}
            onClick={() => {
              this.setState({ searchQuery: "", content: [] });
              this.search("");
            }}
          />
        </div>
        {
          this.state.content.length !== 0 &&
          this.state.content.map(
            (info: IPeopleInfo, index): JSX.Element => {
              return (
                <div
                  key={"peopleCard" + index}
                  className={styles.peopleCardDiv} >
                  <PeopleCard
                    index={index}
                    properties={this.getPropertiesForPeopleCard(info.properties)}
                    image={info.image}
                    linkedTextColor={this.props.highlightColor}
                    backgroundColor={"transparent"}
                    tenant={this.props.tenant}
                    isMobile={this.props.isMobile}
                  />
                </div>
              );
            }
          )
        }
        {!this.state.isLoading && !this.state.loadingMore && !this.state.hasAllPeople &&
          <div
            className={styles.loadMoreButton}
            style={{
              color: this.props.highlightColor
            }}
            onClick={() => {
              this.search(this.state.searchQuery, this.state.content.length);
            }}
          >
            {new LocalizationService().strings.Phonebook_LoadMore}
          </div>
        }
        {(this.state.isLoading || this.state.loadingMore) &&
          <div className={styles.IA_loading}>
            <div className={styles.loader} />
          </div>
        }
      </div>
    );
    return (
      <div id="phonebook" className={styles.phonebook}>
        {table}
      </div>
    );
  }

  
}


